<script setup lang="ts">
import FormTextarea from '@/core/forms/FormTextarea.vue';
import { NOTE_TEXT_MAX_LENGTH } from 'common/constants/note.constants';
import { computed, ref } from 'vue';

const emit = defineEmits<{
  'apply-to-all': [value: string];
  commit: [value: string];
  'update:value': [value: string];
}>();

const props = withDefaults(
  defineProps<{
    applyToAllLoading?: boolean;
    disabled?: boolean;
    name: string;
    showApplyToAll?: boolean;
    value: string;
  }>(),
  {
    applyToAllLoading: false,
    disabled: false,
    showApplyToAll: false,
  },
);

const editing = ref(false);
const canShowApplyToAll = computed(
  () => props.showApplyToAll && !props.disabled && editing.value && props.value.trim().length > 0,
);

const handleFocusOut = (event: FocusEvent): void => {
  const editor = event.currentTarget;
  if (!(editor instanceof HTMLElement)) return;
  if (event.relatedTarget instanceof Node && editor.contains(event.relatedTarget)) return;
  editing.value = false;
};
</script>

<template>
  <div class="flex flex-col" @focusin="editing = true" @focusout="handleFocusOut">
    <FormTextarea
      :name="name"
      auto-resize
      :commit-on-enter="false"
      :disabled="disabled"
      :maxlength="NOTE_TEXT_MAX_LENGTH"
      :value="value"
      :rows="1"
      theme="surface"
      :placeholder="$t('note.addAnnotationsPlaceholder')"
      @update:value="emit('update:value', $event)"
      @commit="emit('commit', $event)"
    />

    <button
      v-if="canShowApplyToAll"
      type="button"
      class="self-end text-footer font-normal leading-[1.6] tracking-[0.01em] text-secondary underline outline-inner transition-colors hover:text-primary active:text-primary disabled:cursor-wait disabled:opacity-40"
      :disabled="applyToAllLoading"
      @pointerdown.prevent
      @click="emit('apply-to-all', value)"
    >
      {{ $t('performanceAssessmentEntry.applyObservationToAll') }}
    </button>
  </div>
</template>
